import type { ElementType, ReactNode } from 'react'
import cn from 'classnames'

import styles from './Typography.module.scss'

/**
 * Six size steps and no more — the console shipped sixteen, including 12/12.5/13/13.5 clusters
 * no eye can separate. `h*` are the display steps, `t*` the text ones, `overline` the only
 * uppercase-with-tracking slot left (it marks a data label, never body copy).
 */
export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 't1'
  | 't2'
  | 't3'
  | 'caption'
  | 'overline'

export type TypographyColor =
  | 'ink'
  | 'ink2'
  | 'muted'
  | 'faint'
  | 'accent'
  | 'heading'
  | 'headingAccent'
  | 'body'
  | 'ok'
  | 'warn'
  | 'bad'
  | 'inherit'

type TypographyProps = {
  variant?: TypographyVariant
  color?: TypographyColor
  component?: ElementType
  align?: 'left' | 'center' | 'right'
  weight?: 400 | 500 | 600 | 700 | 800
  tabular?: boolean
  className?: string
  children?: ReactNode
  id?: string
}

const defaultTag: Record<TypographyVariant, ElementType> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  t1: 'p',
  t2: 'p',
  t3: 'p',
  caption: 'span',
  overline: 'span',
}

export const Typography = ({
  variant = 't2',
  color = 'ink',
  component,
  align,
  weight,
  tabular,
  className,
  children,
  id,
}: TypographyProps) => {
  const Component = component ?? defaultTag[variant]

  return (
    <Component
      id={id}
      className={cn(styles.root, styles[variant], styles[`color-${color}`], className, {
        [styles.tabular as string]: tabular,
      })}
      style={{ textAlign: align, fontWeight: weight }}
    >
      {children}
    </Component>
  )
}
